'use client';
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

export type NavigationLayout = 'sidebar' | 'compact';
export interface NavigationPolicy {
  defaultLayout: NavigationLayout;
  allowedLayouts: NavigationLayout[];
  allowUserChoice: boolean;
}
const fallbackPolicy: NavigationPolicy = { defaultLayout: 'sidebar', allowedLayouts: ['sidebar', 'compact'], allowUserChoice: true };

interface NavContextValue {
  isNavigating: boolean;
  navLabel: string;
  startNavigation: (label?: string) => void;
  endNavigation: () => void;
  layout: NavigationLayout;
  effectiveLayout: NavigationLayout;
  policy: NavigationPolicy;
  canManageNavigationPolicy: boolean;
  navigationLoading: boolean;
  navigationSaving: boolean;
  navigationError: string | null;
  saveNavigationLayout: (layout: NavigationLayout) => Promise<void>;
  updateNavigationPolicy: (policy: Partial<NavigationPolicy> & { clearNavigationChoices?: boolean }) => Promise<void>;
}

const NavCtx = createContext<NavContextValue>({
  isNavigating: false,
  navLabel: 'Loading…',
  startNavigation: () => {},
  endNavigation: () => {},
  layout: 'sidebar', effectiveLayout: 'sidebar', policy: fallbackPolicy,
  canManageNavigationPolicy: false, navigationLoading: true, navigationSaving: false,
  navigationError: null, saveNavigationLayout: async () => {}, updateNavigationPolicy: async () => {},
});

export function NavigationProvider({ children }: { children: React.ReactNode }) {
  const [isNavigating, setIsNavigating] = useState(false);
  const [navLabel, setNavLabel] = useState('Loading…');
  const [layout, setLayout] = useState<NavigationLayout>('sidebar');
  const [policy, setPolicy] = useState<NavigationPolicy>(fallbackPolicy);
  const [canManageNavigationPolicy, setCanManageNavigationPolicy] = useState(false);
  const [navigationLoading, setNavigationLoading] = useState(true);
  const [navigationSaving, setNavigationSaving] = useState(false);
  const [navigationError, setNavigationError] = useState<string | null>(null);

  const startNavigation = useCallback((label?: string) => {
    setNavLabel(label || 'Loading…');
    setIsNavigating(true);
  }, []);

  const endNavigation = useCallback(() => {
    setIsNavigating(false);
    setNavLabel('Loading…');
  }, []);

  useEffect(() => {
    void fetch('/api/user/preferences')
      .then(async (response) => {
        if (!response.ok) throw new Error('Unable to load navigation preferences');
        const data = await response.json();
        const nextPolicy = { ...fallbackPolicy, ...(data.navigationPolicy || {}) };
        setPolicy(nextPolicy);
        const saved = data.navigationPreferences?.layout;
        setLayout(nextPolicy.allowedLayouts.includes(saved) ? saved : nextPolicy.defaultLayout);
        setCanManageNavigationPolicy(Boolean(data.canManageThemePolicy));
      })
      .catch((error) => setNavigationError(error.message))
      .finally(() => setNavigationLoading(false));
  }, []);

  const effectiveLayout = policy.allowUserChoice && policy.allowedLayouts.includes(layout)
    ? layout
    : policy.defaultLayout;
  const saveNavigationLayout = async (next: NavigationLayout) => {
    if (!policy.allowUserChoice || !policy.allowedLayouts.includes(next)) return;
    setNavigationSaving(true); setNavigationError(null);
    try {
      const response = await fetch('/api/user/preferences', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ navigationPreferences: { layout: next } }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Unable to save navigation layout');
      }
      setLayout(next);
    } catch (error: any) { setNavigationError(error.message); throw error; }
    finally { setNavigationSaving(false); }
  };
  const updateNavigationPolicy = async (patch: Partial<NavigationPolicy> & { clearNavigationChoices?: boolean }) => {
    setNavigationSaving(true); setNavigationError(null);
    try {
      const response = await fetch('/api/admin/theme-policy', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          defaultLayout: patch.defaultLayout,
          allowedLayouts: patch.allowedLayouts,
          allowNavigationChoice: patch.allowUserChoice,
          clearNavigationChoices: patch.clearNavigationChoices,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || 'Unable to save navigation policy');
      }
      const data = await response.json();
      setPolicy(data.navigationPolicy);
      if (patch.clearNavigationChoices) setLayout(data.navigationPolicy.defaultLayout);
    } catch (error: any) { setNavigationError(error.message); throw error; }
    finally { setNavigationSaving(false); }
  };

  const value = useMemo(() => ({
    isNavigating, navLabel, startNavigation, endNavigation, layout, effectiveLayout, policy,
    canManageNavigationPolicy, navigationLoading, navigationSaving, navigationError,
    saveNavigationLayout, updateNavigationPolicy,
  }), [isNavigating, navLabel, layout, effectiveLayout, policy, canManageNavigationPolicy, navigationLoading, navigationSaving, navigationError]);
  return (
    <NavCtx.Provider value={value}>
      {children}
    </NavCtx.Provider>
  );
}

export function useNavigation() {
  return useContext(NavCtx);
}
